/**
 * PresetCard Component
 *
 * Individual preset card for the full-page presets dialog.
 * Shows preset details, collapsible enhancements, collapsible inline
 * tag management, and load/delete actions.
 */

import { memo } from 'react';
import { Trash2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { TagEditor } from './TagEditor';
import type { ImagePreset, TagDefinition } from '../types/image-generator.types';
import { ENHANCEMENT_CATEGORIES } from '../types/image-generator.types';

export interface PresetCardProps {
  preset: ImagePreset;
  tagDefinitions: TagDefinition[];
  sizeOptions: { value: string; label: string }[];
  onLoadPreset: (preset: ImagePreset) => void;
  onDeletePreset: (presetId: string) => void;
  onUpdatePreset: (
    presetId: string,
    updates: Partial<Pick<ImagePreset, 'tags' | 'categoryId'>>,
  ) => void;
  onCreateTag: (name: string) => string;
}

export const PresetCard = memo(function PresetCard({
  preset,
  tagDefinitions,
  sizeOptions,
  onLoadPreset,
  onDeletePreset,
  onUpdatePreset,
  onCreateTag,
}: PresetCardProps) {
  const assignedTags = tagDefinitions.filter((t) => preset.tags.includes(t.id));
  const hasEnhancements = ENHANCEMENT_CATEGORIES.some((cat) =>
    preset.enhancements[cat.key]?.trim(),
  );

  return (
    <div className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors">
      {/* Header: Name + Delete */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium truncate">{preset.name}</h4>
        </div>
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDeletePreset(preset.id)}
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            title="Delete preset"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Settings labels */}
      <div className="text-xs text-muted-foreground space-y-0.5 mb-3">
        <p>Size: {sizeOptions.find((s) => s.value === preset.size)?.label || preset.size}</p>
        {(preset.providerParams?.dalle_quality ||
          preset.providerParams?.gpt_quality ||
          preset.quality) && (
          <p>
            Quality:{' '}
            {String(
              preset.providerParams?.dalle_quality ||
                preset.providerParams?.gpt_quality ||
                preset.quality,
            )}
          </p>
        )}
        {(preset.providerParams?.dalle_style ||
          preset.providerParams?.recraft_style ||
          preset.providerParams?.stability_style ||
          preset.style) && (
          <p>
            Style:{' '}
            {String(
              preset.providerParams?.dalle_style ||
                preset.providerParams?.recraft_style ||
                preset.providerParams?.stability_style ||
                preset.style,
            )}
          </p>
        )}
      </div>

      {/* Collapsible enhancements */}
      {hasEnhancements && (
        <Collapsible className="mb-3">
          <CollapsibleTrigger className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors group">
            <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
            <span>Prompt Enhancements</span>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2 pl-4 space-y-1 text-xs">
            {ENHANCEMENT_CATEGORIES.filter((cat) => preset.enhancements[cat.key]?.trim()).map(
              (cat) => (
                <div key={cat.key}>
                  <span className="text-muted-foreground">{cat.label}:</span>{' '}
                  <span className="text-foreground">{preset.enhancements[cat.key]}</span>
                </div>
              ),
            )}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Collapsible Tags section */}
      <Collapsible className="mb-3">
        <CollapsibleTrigger className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors group">
          <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
          <span>Tags{assignedTags.length > 0 ? ` (${assignedTags.length})` : ''}</span>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 pl-4">
          <TagEditor
            assignedTagIds={preset.tags}
            tagDefinitions={tagDefinitions}
            onAssignTag={(tagId) => {
              if (!preset.tags.includes(tagId)) {
                onUpdatePreset(preset.id, { tags: [...preset.tags, tagId] });
              }
            }}
            onUnassignTag={(tagId) => {
              onUpdatePreset(preset.id, { tags: preset.tags.filter((t) => t !== tagId) });
            }}
            onCreateTag={(name) => {
              const newTagId = onCreateTag(name);
              onUpdatePreset(preset.id, { tags: [...preset.tags, newTagId] });
              return newTagId;
            }}
          />
        </CollapsibleContent>
      </Collapsible>

      {/* Load button */}
      <Button variant="outline" size="sm" onClick={() => onLoadPreset(preset)} className="w-full">
        Open
      </Button>
    </div>
  );
});
